import { useEffect, useMemo, useState } from 'react';
import type { Account } from '../../core/accounts';
import type { Category } from '../../core/categories';
import { formatDateKey } from '../../core/dates';
import { EMPTY_FILTERS, applyFilters, totalsFor, type Filters } from '../../core/filters';
import { formatPaise } from '../../core/money';
import type { Period } from '../../core/period';
import { isLiabilityPayment, type Transaction, type TransactionInput } from '../../core/transactions';
import { watchAccounts } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';
import { watchCategories, watchTags, type Tag } from '../../data/categories';
import {
  createTransaction,
  queryForFilters,
  queryKey,
  softDeleteTransaction,
  updateTransaction,
  watchTransactions,
} from '../../data/transactions';
import { withDefaults } from '../../data/preferences';
import { FilterBar } from '../components/FilterBar';
import { TransactionForm } from '../transactions/TransactionForm';

interface Props {
  record: UserRecord;
  period: Period;
  isPhone: boolean;
  /** Phone opens straight into entry when arriving via Add (WD-01). */
  startInEntry?: boolean;
}

export function TransactionsScreen({ record, period, isPhone, startInEntry }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [filters, setFilters] = useState<Filters>(() => EMPTY_FILTERS(period));
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [entering, setEntering] = useState(Boolean(startInEntry));
  const [error, setError] = useState<string | null>(null);

  const prefs = withDefaults(record.preferences);

  useEffect(() => watchAccounts(setAccounts), []);
  useEffect(() => watchCategories(setCategories), []);
  useEffect(() => watchTags(setTags), []);

  // The global period selector governs this screen too, so a change up there reshapes
  // the query down here rather than leaving a stale filter behind.
  useEffect(() => setFilters((f) => ({ ...f, period })), [period]);

  // One place decides what Firestore is asked for, shared with the transaction list and
  // reports (`queryForFilters`). `spec` is rebuilt every render, so the effect depends on
  // its stable string form rather than on its identity.
  // Keyed on the query's own shape, not on `filters`: typing in the search box changes
  // the filters but not what Firestore is asked for, and re-subscribing on every
  // keystroke would flash the list back to "Loading…" for nothing.
  const specKey = queryKey(queryForFilters(filters));
  const spec = useMemo(() => queryForFilters(filters), [specKey]);
  useEffect(() => {
    setTransactions(null);
    return watchTransactions(
      spec,
      (list) => {
        setTransactions(list);
        setError(null);
      },
      (cause) => {
        setTransactions([]);
        setError(cause.message);
      },
    );
  }, [spec]);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const visible = useMemo(
    () => (transactions ? applyFilters(transactions, filters) : []),
    [transactions, filters],
  );
  const totals = useMemo(() => totalsFor(visible, accountType), [visible, accountType]);

  const nameOf = (id: string | null) =>
    id ? (accounts.find((a) => a.id === id)?.name ?? id) : '';
  const categoryName = (id: string | null) =>
    id ? (categories.find((c) => c.id === id)?.name ?? id) : '';

  async function save(input: TransactionInput) {
    setError(null);
    try {
      if (editing) {
        await updateTransaction(editing, input, record.uid, accountType);
        setEditing(null);
      } else {
        await createTransaction(input, record.uid, accountType);
        if (isPhone) setEntering(false);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save that.');
    }
  }

  async function remove(txn: Transaction) {
    setError(null);
    try {
      await softDeleteTransaction(txn, record.uid, accountType);
      setEditing(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not delete that.');
    }
  }

  const form = (
    <TransactionForm
      key={editing?.id ?? 'new'}
      accounts={accounts}
      categories={categories}
      tags={tags}
      defaultAccountId={prefs.defaultAccountId}
      defaultCurrency={prefs.defaultCurrency}
      editing={editing}
      compact={isPhone}
      onSave={save}
      onCancel={editing ? () => setEditing(null) : isPhone ? () => setEntering(false) : undefined}
      onDelete={editing ? () => void remove(editing) : undefined}
    />
  );

  // ---- Phone: entry is a full screen, not a panel beside a table (FR-TXN-15) ----
  if (isPhone && (entering || editing)) {
    return (
      <>
        <div className="head-row">
          <h1>{editing ? 'Edit' : 'Add'}</h1>
        </div>
        {error && <p className="error">{error}</p>}
        {form}
      </>
    );
  }

  return (
    <div className={isPhone ? undefined : 'txn-layout'}>
      <div className="txn-main">
        <div className="head-row">
          <h1>Transactions</h1>
          {isPhone && (
            <button type="button" className="btn btn-primary" onClick={() => setEntering(true)}>
              Add
            </button>
          )}
        </div>

        <FilterBar
          filters={filters}
          accounts={accounts}
          categories={categories}
          tags={tags}
          onChange={setFilters}
        />

        {error && <p className="error">{error}</p>}

        {transactions === null ? (
          <p className="muted">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="placeholder">
            {transactions.length === 0
              ? 'No transactions in this period yet.'
              : 'No transactions match these filters.'}
          </div>
        ) : (
          <table className="table txn-table">
            <tbody>
              {visible.map((txn) => (
                <tr
                  key={txn.id}
                  onClick={() => setEditing(txn)}
                  className={editing?.id === txn.id ? 'selected' : undefined}
                >
                  <td className="date">{formatDateKey(txn.dateKey, 'dd MMM')}</td>
                  <td>
                    <strong>{txn.description || categoryName(txn.categoryId) || '—'}</strong>
                    <div className="muted tiny">
                      {txn.type === 'transfer'
                        ? `${nameOf(txn.accountId)} → ${nameOf(txn.toAccountId)}${
                            isLiabilityPayment(txn, accountType) ? ' · payment' : ''
                          }`
                        : `${nameOf(txn.accountId)}${
                            txn.categoryId ? ` · ${categoryName(txn.categoryId)}` : ''
                          }`}
                      {txn.isBusiness && ' · business'}
                    </div>
                  </td>
                  <td className={`num ${txn.type}`}>
                    {txn.type === 'income' ? '+' : txn.type === 'expense' ? '−' : ''}
                    {formatPaise(txn.amountPaise, { currency: txn.currency, symbol: false })}
                    {txn.amountExpression && (
                      <div className="muted tiny" title={txn.amountExpression}>
                        {txn.amountExpression}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {visible.length > 0 && (
          <p className="muted totals-row">
            {totals.count} rows · income <strong>{formatPaise(totals.incomePaise)}</strong> ·
            expenses <strong>{formatPaise(totals.expensePaise)}</strong>
            {totals.transferPaise > 0 && (
              <> · transfers {formatPaise(totals.transferPaise)}<span className="tiny"> (in neither)</span></>
            )}
          </p>
        )}
      </div>

      {/* Desktop: the form lives beside the table permanently (WD-02). Not a modal —
          the surrounding rows are context, and entering ten in a row should never
          require reopening anything. */}
      {!isPhone && (
        <aside className="txn-side">
          <h2>{editing ? 'Edit transaction' : 'New transaction'}</h2>
          {form}
        </aside>
      )}
    </div>
  );
}
